import type { ReactNode } from 'react';
import { FormatNumber, Grid, GridItem, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { ProgramWithStats } from '@fluenceur/types';
import { browseColors } from './browse-tokens';

interface ProgramStatGridProps {
  program: ProgramWithStats;
  currency: string;
}

/** Four-up stat row (affiliates / visits / orders / revenue) for a program card. */
const ProgramStatGrid = ({ program, currency }: ProgramStatGridProps) => {
  const { t } = useTranslation();

  const stats: { label: string; value: ReactNode; accent?: boolean }[] = [
    {
      label: t('programs.browse.stats.affiliates'),
      value: <FormatNumber value={program.affiliatesCount ?? 0} />,
    },
    {
      label: t('programs.browse.stats.visits'),
      value: <FormatNumber value={program.visits ?? 0} />,
    },
    {
      label: t('programs.browse.stats.orders'),
      value: <FormatNumber value={program.orders ?? 0} />,
    },
    {
      label: t('programs.browse.stats.revenue'),
      value: <FormatNumber value={program.revenue ?? 0} style="currency" currency={currency} />,
      accent: true,
    },
  ];

  return (
    <Grid mt="14px" templateColumns="repeat(4, 1fr)" gap="8px">
      {stats.map((stat) => (
        <GridItem key={stat.label} minW={0}>
          <Text
            fontSize="10.5px"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing="0.06em"
            color={browseColors.statLabel}
            truncate
          >
            {stat.label}
          </Text>
          <Text
            mt="3px"
            fontSize="14px"
            fontWeight={600}
            color={stat.accent ? browseColors.revenueValue : browseColors.statValue}
            truncate
          >
            {stat.value}
          </Text>
        </GridItem>
      ))}
    </Grid>
  );
};

export default ProgramStatGrid;
